// Wording for the cancellation dialog, kept in one place so every promise it makes can be reviewed
// against what billing actually does at period end.

// `pause` offers a break before the cancel and `pauseReview` confirms one; `offer` answers "too expensive"
// in the place of `support`, and `offerTaken` closes the visit when they take it.
export type CancellationStep = 'pause' | 'pauseReview' | 'reason' | 'support' | 'offer' | 'offerTaken' | 'confirm' | 'done';

/** Where each counted step sits on the progress bar; the offer and the help share the middle. */
export const PROGRESS_POSITION: Partial<Record<CancellationStep, number>> = { reason: 0, support: 1, offer: 1, confirm: 2 };
export const PROGRESS_TOTAL = 3;

export const stepLabel = (step: number, total: number) => `Step ${step} of ${total}`;

export const KEEP_PLAN = 'Keep my plan';
export const BACK = 'Back';
export const CLOSE_LABEL = 'Close';

const days = (count: number) => `${count} days`;

// Both offers are priced against billing as it was on opening; a change since means a fresh look.
const BILLING_CHANGED = 'Your billing changed while this was open. Check the details and confirm again.';

// Free and reversible, so it comes before the reasons; every cost is shown on the review that follows.
export const PAUSE_STEP = {
  loading: 'Loading your options',
  title: 'Take a break instead?',
  description: (count: number, plans: number) =>
    `Put your ${plans === 1 ? 'plan' : 'plans'} on hold for ${days(count)}. No payments during that time.`,
  noCharge: 'No payments while paused',
  unit: (count: number) => (count === 1 ? 'day' : 'days'),
  starts: 'Pause starts',
  today: 'Today',
  restarts: 'Plans and billing restart',
  perks: [
    { key: 'credit', title: 'Credit kept', body: 'Unused time counts toward your restart' },
    { key: 'setup', title: 'Setup saved', body: 'Accounts, copiers and settings stay' },
    { key: 'early', title: 'Back any time', body: 'Restart sooner from your billing page' }
  ],
  footnote: 'You see exactly what changes before anything is paused.',
  // The first screen only leads to the review; the review's button is the one that pauses.
  review: 'Review pause details',
  pause: (count: number) => `Pause for ${days(count)}`,
  continue: 'Continue cancelling'
} as const;

/** What a refusal can name: the day it lifts, and how many pauses a year allows. */
export type PauseBlockerFacts = { date?: string; limit?: number };
type PauseBlockerCopy = { title: string; detail: (facts: PauseBlockerFacts) => string };
const opensOn = (date?: string) => (date ? ` Your next pause opens on ${date}.` : '');

// Why no pause is offered, keyed by billing-manager's reason codes: what stands in the way, and what clears it.
export const PAUSE_UNAVAILABLE_STEP = {
  title: "A pause isn't available yet",
  description: "Here's what stands in the way, and what would make a pause possible.",
  group: "What's in the way",
  helpNote: 'Not sure what to do?',
  reasons: {
    unsupported_ea_hosting: {
      title: 'You have EA hosting',
      detail: () => "Your EA keeps running on our servers, so a pause can't cover it. Pausing opens up once EA hosting has ended."
    },
    trial_subscription: {
      title: 'A plan is still on its free trial',
      detail: () => "Trials can't be paused. Pausing opens up once the trial turns into a paid plan."
    },
    freeze_cycle_limit_reached: {
      title: 'No pauses left for now',
      detail: ({ date, limit }) => `You've used ${limit ? `all ${limit}` : 'all the'} pauses allowed in a year.${opensOn(date)}`
    },
    freeze_resume_cooldown_active: {
      title: 'You came back from a pause recently',
      detail: ({ date }) => `There's a short wait after coming back before the next pause.${opensOn(date)}`
    },
    payment_method_required: {
      title: 'No primary card',
      detail: () => 'Add a card, or set one as primary, under Payment Method on your billing page. It pays the restart when the pause ends.'
    },
    billing_delinquent: {
      title: 'A payment is overdue',
      detail: () => 'Pay the overdue invoice from your billing page first.'
    },
    unresolved_subscription_billing: {
      title: "A plan's billing isn't settled",
      detail: () => "One of your plans has a payment or change that hasn't gone through yet. Settle it from your billing page first."
    },
    subscription_renewing: {
      title: 'A plan is about to renew',
      detail: () =>
        'One of your plans renews within the next couple of hours, or its renewal is still being paid. Pausing opens again once the renewal has gone through.'
    },
    unresolved_crypto_payment: {
      title: 'A crypto payment is pending',
      detail: () => 'Finish or cancel the pending crypto payment first.'
    },
    invoice_billed_subscription: {
      title: 'A plan is paid by invoice',
      detail: () => "Plans paid by invoice, such as with crypto, can't be paused."
    },
    unsupported_subscription_tax: {
      title: 'A plan has tax settings',
      detail: () => "Plans with tax settings can't be paused yet. Support can help."
    },
    normal_subscription_required: {
      title: 'No paid plan to pause',
      detail: () => 'A pause needs at least one active paid plan.'
    },
    subscriptions_ending: {
      title: 'Your plans are already set to end',
      detail: () => "They run until their end date, so there's nothing to pause."
    },
    freeze_already_open: {
      title: 'You already have a pause',
      detail: () => 'You can see it, and come back early, from your billing page.'
    }
  } as Record<string, PauseBlockerCopy>,
  unknown: { title: 'Not available on your account right now', detail: () => 'Support can tell you why.' } as PauseBlockerCopy
} as const;

const accounts = (count: number) => `${count} ${count === 1 ? 'account' : 'accounts'}`;

// What a pause does to open trades, said wherever the review speaks of them. The check finds copied trades,
// the ones we copy and protect. Closes are master-driven, so only a master close during the pause is lost.
const PAUSED_UNMANAGED = 'While your subscription is paused, we are unable to copy, protect or close trades on your accounts.';
const COPIES_STAY_OPEN =
  'If a master trade closes during the pause, its copies stay open with your broker until you close them, even after you resume.';

// Every money and access consequence of a pause, said before it happens: nothing here may promise more than billing does.
export const PAUSE_REVIEW_STEP = {
  title: 'Before you pause',
  description: 'Here is exactly what happens, and when.',
  pausedUntil: 'Paused until',
  restartNote: 'Then your plans restart and billing resumes by itself. You can come back sooner from your billing page.',
  pausedGroup: 'Paused until then',
  keptGroup: 'Kept for you',
  kept: {
    setup: 'Your accounts, copiers and settings stay saved',
    credit: 'The unused part of your current billing period is kept as credit'
  },
  restartGroup: (date: string) => `On ${date}`,
  restart: {
    // The amount is billing-manager's price at today's rates; without it the line still says how it is worked out.
    charge: (amount?: string) =>
      amount
        ? `We charge ${amount} at today's prices: a new billing period, less your credit. Your billing date moves to that day.`
        : 'A new billing period starts, charged less your credit. Your billing date moves to that day.',
    declined:
      'If that payment is declined, your plans stay paused until it is paid. Left unpaid, they are cancelled on the date in our email.'
  },
  leaveGroup: 'If you decide not to come back',
  offerTitle: 'Pausing gives up your discount',
  offerBody: 'The discount waiting for your next payment is lost when the pause starts. Keep your plans running to use it.',
  leave: 'You can cancel while paused. Your plans end then, and the unused time stays as credit on your account. It is not refunded.',
  acknowledge: (date: string) =>
    `I understand billing restarts by itself on ${date}, and that if I cancel instead, my unused time stays as account credit and is not refunded.`,
  busy: 'Pausing…',
  checkingTrades: 'Checking your accounts for open copied trades…',
  // The pause never closes a trade: it only warns, so a closed market or a broker outage never blocks it.
  openTradesTitle: (count: number) => `Open copied trades on ${accounts(count)}`,
  // Only accounts holding one are listed, so the list says so rather than read as all of them.
  openTradesLead: 'Copied trades are still open on these accounts:',
  openTradesBody: `${PAUSED_UNMANAGED} ${COPIES_STAY_OPEN}`,
  // Some accounts could not be checked: named, so the customer checks those themselves.
  someUncheckedTitle: (count: number) => `We couldn't check ${accounts(count)} for copied trades`,
  someUncheckedBody: `Please check these accounts with your broker before pausing. ${COPIES_STAY_OPEN}`,
  tradesUncheckedTitle: "We couldn't check your accounts for copied trades",
  tradesUncheckedBody: `Please check your accounts with your broker before pausing. ${PAUSED_UNMANAGED} ${COPIES_STAY_OPEN}`,
  noCopiedTrades: `We found no open copied trades. ${PAUSED_UNMANAGED} Any trade you leave open remains open with your broker until you close it.`,
  // Why Pause is not available yet, under the review.
  acknowledgeFirst: 'Tick the box above to confirm.',
  billingChanged: BILLING_CHANGED
} as const;

export const REASON_STEP = {
  title: (name: string) => (name ? `${name}, why would you like to cancel?` : 'Why would you like to cancel?'),
  description: 'Select one option to help us tailor the next step.',
  legend: 'Why would you like to cancel?',
  commentHelper: 'Optional',
  continue: 'Continue'
} as const;

export const REASON_COMMENT_MAX_LENGTH = 1000;

const period = (interval: string, count: number) => (count === 1 ? interval : `${count} ${interval}s`);

// The "too expensive" offer: one discount, on the customer's next payment. Every figure comes from Stripe through
// billing-manager.
export const OFFER_STEP = {
  loading: 'Loading your offer',
  title: (percent: string) => `Take ${percent} off your next payment`,
  description: (percent: string) => `Since price is the reason, here's ${percent} off your next payment if you stay.`,
  headline: (percent: string) => `${percent} off`,
  pill: 'Next payment',
  renewsOn: (date?: string) => (date ? `Renews ${date}` : 'Your next renewal'),
  yourPrice: 'Your price',
  insteadOf: 'Instead of',
  commitment: 'Commitment',
  cancelAnytime: 'Cancel anytime',
  pricePer: (price: string, interval: string, count: number) => `${price}/${period(interval, count)}`,
  note: {
    title: 'Used once',
    body: "On whichever comes first: this renewal, or a new plan you buy before it. Adding seats doesn't use it."
  },
  use: 'Use offer',
  busy: 'Applying…',
  cancelAnyway: 'Cancel anyway',
  billingChanged: BILLING_CHANGED,
  alreadyTaken: 'Your account has already taken this offer, and it can only be taken once.'
} as const;

export const OFFER_TAKEN_STEP = {
  title: 'Your discount is waiting',
  description: (percent: string) =>
    `Your plans carry on as before. ${percent} comes off your next payment, or a new plan if you buy one first. You can still cancel any time.`,
  done: 'Done'
} as const;

export const SUPPORT_STEP = {
  waitingTitle: (percent: string) => `Your ${percent} off is still waiting`,
  waitingBody: (date?: string) =>
    `${
      date ? `It comes off your next payment, your renewal on ${date}.` : 'It comes off your next payment.'
    } If you cancel, it only comes off a new plan you buy later.`,
  youToldUs: 'You told us',
  quote: (text: string) => `“${text}”`,
  continue: 'No, continue'
} as const;

// The design's last question: keeping is the main action, the cancel sits behind the tick.
export const CONFIRM_STEP = {
  title: (name?: string) => (name ? `Cancel ${name} subscription?` : 'Cancel your subscriptions?'),
  description: (plans: number) =>
    plans === 1
      ? 'Your plan stays active until the end of the current billing period. No further charges will be made after that.'
      : 'Your plans stay active until the end of their current billing periods. No further charges will be made after that.',
  activeUntilPrefix: 'Everything stays active until',
  activeUntilEach: 'Everything stays active until the end of the period you have paid for',
  undoNote: 'You can undo this from billing until then.',
  plansEnding: 'Plans ending',
  whatStops: 'What stops with them',
  periodEnd: 'Period end',
  acknowledge: 'I understand that everything listed above stops when the paid period ends.',
  cancel: 'Cancel at period end',
  busy: 'Cancelling…'
} as const;

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export const PLAN_GROUPS = {
  copier: 'Copier',
  rooms: 'Signal Manager',
  environmentsTitle: 'Dedicated Environments',
  environments: (count: number) => plural(count, 'environment', 'environments'),
  roomCount: (count: number) => plural(count, 'room', 'rooms'),
  accounts: (count: number) => plural(count, 'account', 'accounts'),
  separator: ' · ',
  from: (date: string) => `From ${date}`
} as const;

// What billing switches off when the last plan ends; each row only shows when it applies.
export const CONSEQUENCES = {
  accounts: 'Your trading accounts switch off and copying stops',
  dedicatedEnv: 'Your dedicated environment is shut down',
  rooms: 'Your rooms close for their members',
  eaHosting: 'Your hosted EAs stop running',
  aiConnect: 'AI Connect stops working',
  analyzer: 'Analyzer stops'
} as const;

// Cancelling from the pause: the plans end now and the money already paid stays as credit, never refunded.
export const PAUSED_CANCEL = {
  open: 'Cancel instead',
  title: 'Cancel instead of coming back?',
  description: (restartsOn: string) => `Your paused plans end today, instead of restarting on ${restartsOn}.`,
  endsGroup: 'What ends today',
  ends: {
    plans: 'Your plans end now and do not restart',
    access: 'Your trading accounts stay switched off, and any rooms close for their members'
  },
  moneyGroup: 'Your money',
  credit: (amount?: string) =>
    amount
      ? `The unused time you had paid for, ${amount}, stays as credit on your account.`
      : 'The unused time you had paid for stays as credit on your account. Our email tells you the amount.',
  noRefund: 'It is not refunded. It is taken off anything you buy from us later.',
  unpaidVoided: 'The restart charge that did not go through is cancelled, so you never pay it.',
  acknowledge: 'I understand my plans end today, and that my unused time stays as account credit and is not refunded.',
  keep: 'Keep my pause',
  cancel: 'Cancel my plans',
  busy: 'Cancelling…',
  changed: 'Your pause changed while this was open. Check it and try again.',
  doneTitle: 'Your plans are cancelled',
  backToBilling: 'Return to billing'
} as const;

export const SUPPORT_LINK = 'Talk to support';

// checkout-v2's overdue drawer can only pay, so closing one is a support request.
export const PAST_DUE_NOTE = 'An overdue subscription is not included here. Pay it from billing, or ask support to close it.';

export const DONE_STEP = {
  title: 'Your cancellation is confirmed',
  description: (date?: string | null) =>
    `${
      date ? `Everything stays active until ${date}.` : 'Everything stays active until the end of its paid period.'
    } You can undo this from billing any time before then.`,
  done: 'Done'
} as const;

export const CANCEL_ERROR_FALLBACK = 'Something went wrong. Please try again.';

/** Some cancelled, some not: the ones left are still listed, so retrying only sends those. */
export const partialCancelMessage = (failed: number, total: number, detail: string) =>
  `${total - failed} of ${total} subscriptions were cancelled; ${failed} could not be. ${detail}`;

export const CRISP_CANCELLED_EVENT = 'Subscription Cancelled';
export const CRISP_CANCELLED_COLOR = 'red';

/** The billing page's way into the dialog: findable, never promoted. */
export const ENTRY = {
  title: 'Cancel your plan',
  description:
    'Ends every subscription at the close of its paid period: copying stops and your trading accounts switch off. You see exactly what changes before anything is cancelled.',
  action: 'Cancel plan',
  nextRenewal: (date: string) => `Next renewal ${date}`
} as const;
